import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { getAuth, onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'; // Import drag-and-drop library
import './AdminDashboard.css';
import Papa from 'papaparse'; // Import Papa for CSV conversion
import jsPDF from 'jspdf'; // Import jsPDF library
import QRCode from 'qrcode'; // Import the QRCode library
import { FaUser, FaCalendarAlt, FaMapMarkerAlt, FaIdBadge, FaClock, FaImage, FaList } from 'react-icons/fa'; // Import all required icons
import emailjs from 'emailjs-com';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage'; // Firebase Storage imports


function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState(null); // Track selected event for scheduling
  const [schedule, setSchedule] = useState({ startTime: '', subFields: [] }); // State for manual schedule
  const [uploadedImage, setUploadedImage] = useState(null); // State for uploaded image
  const [savedImage, setSavedImage] = useState(null); // State for saved image from Firestore
  const [isEventModalOpen, setIsEventModalOpen] = useState(false); // State to toggle event modal visibility
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false); // State to toggle announcement modal
  const navigate = useNavigate(); // Initialize useNavigate
const [announcementText, setAnnouncementText] = useState(''); // State for announcement text
const [hiddenEvents, setHiddenEvents] = useState({}); // Track hidden state for each event
const [eventToDelete, setEventToDelete] = useState(null); // Track the event ID to delete
const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false); // State to toggle schedule modal visibility
const [scheduleToDelete, setScheduleToDelete] = useState(null); // Track the event ID for schedule deletion
const [isEditingProfile, setIsEditingProfile] = useState(false); // State to toggle profile editing
const [profileData, setProfileData] = useState({
  name: user?.name || '',
  company: user?.company || '',
  email: user?.email || '',
}); // State for profile form data

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            setUser({
              name: userDoc.data().name || 'N/A',
              email: currentUser.email,
              company: userDoc.data().company || 'N/A',
            });
          } else {
            setUser({
              name: 'N/A',
              email: currentUser.email,
              company: 'N/A',
            });
          }

          const eventsQuery = query(
            collection(db, 'events'),
            where('createdBy', '==', currentUser.email)
          );
          const querySnapshot = await getDocs(eventsQuery);

          if (!querySnapshot.empty) {
            const userEvents = querySnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setEvents(userEvents);
          } else {
            setEvents([]);
          }
        } catch (error) {
          console.error('Error fetching user data or events:', error);
        }
      } else {
        setUser(null);
        setEvents([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleDeleteEvent = async (eventId) => {
    try {
      await deleteDoc(doc(db, 'events', eventId));
      setEvents(events.filter((event) => event.id !== eventId));
      alert('Event deleted successfully!');
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete the event.');
    }
  };

  const handleDeleteSchedule = async (eventId) => {
    try {
      const eventRef = doc(db, 'events', eventId);
      await updateDoc(eventRef, { schedule: null, scheduleImage: null }); // Remove both schedule and schedule image
      setSavedImage(null); // Clear saved image state
      alert('Schedule deleted successfully!');
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('Failed to delete the schedule.');
    }
  };

  const handlePresentEvent = (eventId) => {
    window.open(`/event/${eventId}`, '_blank');
  };

  const handleAddSubField = () => {
    setSchedule((prev) => ({
      ...prev,
      subFields: [...prev.subFields, { time: '', prize: '', winningTicket: '' }],
    }));
  };

  const handleRemoveSubField = (index) => {
    setSchedule((prev) => ({
      ...prev,
      subFields: prev.subFields.filter((_, i) => i !== index),
    }));
  };

  const handleScheduleChange = (index, field, value) => {
    const updatedSubFields = [...schedule.subFields];
    updatedSubFields[index][field] = value || 'N/A'; // Default to 'N/A' if the field is empty
    setSchedule((prev) => ({ ...prev, subFields: updatedSubFields }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      alert('Please select an image to upload.');
      return;
    }
  
    const storage = getStorage();
    const storageRef = ref(storage, `schedules/${file.name}`); // Save the image in the "schedules" folder
  
    try {
      // Upload the file to Firebase Storage
      await uploadBytes(storageRef, file);
  
      // Get the publicly accessible URL
      const downloadURL = await getDownloadURL(storageRef);
  
      // Save the URL to Firestore (optional)
      const eventRef = doc(db, 'events', selectedEventId);
      await updateDoc(eventRef, { scheduleImage: downloadURL });
  
      // Update the state with the uploaded image URL
      setSavedImage(downloadURL);
      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    }
  };

  const handleDeleteImage = async () => {
    try {
      const eventRef = doc(db, 'events', selectedEventId);
      await updateDoc(eventRef, { scheduleImage: null }); // Remove the saved image from Firestore
      setSavedImage(null); // Clear saved image state
      alert('Image deleted successfully!');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete the image.');
    }
  };

  const handleSaveSchedule = async () => {
    if (!selectedEventId) {
      alert('Please select an event.');
      return;
    }

    try {
      const eventRef = doc(db, 'events', selectedEventId);

      if (uploadedImage) {
        const imageUrl = URL.createObjectURL(uploadedImage); // Mock URL
        await updateDoc(eventRef, { scheduleImage: imageUrl });
        setSavedImage(imageUrl); // Update saved image state
        alert('Schedule image uploaded successfully!');
      } else {
        await updateDoc(eventRef, { schedule });
        alert('Schedule saved successfully!');
      }

      setSchedule({ startTime: '', subFields: [] });
      setUploadedImage(null);
      setSelectedEventId(null);
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Failed to save the schedule.');
    }
  };

  const handleEditSchedule = async (eventId) => {
    try {
      const eventRef = doc(db, 'events', eventId);
      const eventDoc = await getDoc(eventRef);

      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        setSchedule(eventData.schedule || { startTime: '', subFields: [] });
        setSavedImage(eventData.scheduleImage || null); // Fetch saved image from Firestore
        setSelectedEventId(eventId);
      } else {
        alert('Event not found!');
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
      alert('Failed to fetch schedule.');
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedSubFields = Array.from(schedule.subFields);
    const [removed] = reorderedSubFields.splice(result.source.index, 1);
    reorderedSubFields.splice(result.destination.index, 0, removed);

    setSchedule((prev) => ({ ...prev, subFields: reorderedSubFields }));
  };

  const handleDeleteEventClick = (eventId) => {
    setEventToDelete(eventId); // Set the event ID to delete
    setIsEventModalOpen(true); // Open the modal
  };
  
  const confirmDeleteEvent = async () => {
    try {
      await handleDeleteEvent(eventToDelete); // Call the delete function
      setIsEventModalOpen(false); // Close the modal
      setEventToDelete(null); // Clear the event ID
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };
  
  const closeEventModal = () => {
    setIsEventModalOpen(false); // Close the modal
    setEventToDelete(null); // Clear the event ID
  };
  
  const handleDeleteScheduleClick = (eventId) => {
    setScheduleToDelete(eventId); // Set the event ID for schedule deletion
    setIsScheduleModalOpen(true); // Open the modal
  };
  
  const confirmDeleteSchedule = async () => {
    try {
      await handleDeleteSchedule(scheduleToDelete); // Call the delete function
      setIsScheduleModalOpen(false); // Close the modal
      setScheduleToDelete(null); // Clear the event ID
    } catch (error) {
      console.error('Error deleting schedule:', error);
    }
  };
  
  const closeScheduleModal = () => {
    setIsScheduleModalOpen(false); // Close the modal
    setScheduleToDelete(null); // Clear the event ID
  };

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };
  
  const handleUpdateProfile = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
  
    if (!currentUser) {
      alert('You must be logged in to update your profile.');
      return;
    }
  
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
  
      if (!userDoc.exists()) {
        alert('User profile does not exist. Creating a new profile...');
        await setDoc(userRef, {
          name: profileData.name,
          company: profileData.company,
          email: profileData.email,
        });
        alert('Profile created successfully!');
      } else {
        await updateDoc(userRef, {
          name: profileData.name,
          company: profileData.company,
          email: profileData.email,
        });
        alert('Profile updated successfully!');
      }
  
      // Update local state
      setUser({
        name: profileData.name,
        company: profileData.company,
        email: profileData.email,
      });
  
      setIsEditingProfile(false); // Exit editing mode
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile.');
    }
  };
  
  const handleResetPassword = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
  
    if (!currentUser) {
      alert('You must be logged in to reset your password.');
      return;
    }
  
    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      alert('Password reset email sent successfully!');
    } catch (error) {
      console.error('Error sending password reset email:', error);
      alert('Failed to send password reset email.');
    }
  };

  const handleOpenAnnouncementModal = () => {
    setIsAnnouncementModalOpen(true);
  };
  
  const handleCloseAnnouncementModal = () => {
    setIsAnnouncementModalOpen(false);
    setAnnouncementText(''); // Clear the announcement text
  };

  const handleHideEvent = (eventId) => {
    setHiddenEvents((prev) => ({ ...prev, [eventId]: true })); // Mark the event as hidden
  };
  
  const handleShowEvent = (eventId) => {
    setHiddenEvents((prev) => ({ ...prev, [eventId]: false })); // Mark the event as visible
  };
  

  const handleSendAnnouncement = async () => {
    if (!selectedEventId) {
      alert('Please select an event to send the announcement.');
      return;
    }
  
    if (!announcementText.trim()) {
      alert('Please enter an announcement.');
      return;
    }
  
    try {
      // Update Firestore with the announcement
      const eventRef = doc(db, 'events', selectedEventId);
      const timestamp = Date.now();
      await updateDoc(eventRef, { 
        announcement: announcementText, 
        announcementTimestamp: timestamp 
      });
  
      // Fetch all users signed up for this event
      const alertsQuery = query(
        collection(db, 'eventAlerts'),
        where('eventId', '==', selectedEventId)
      );
      const alertsSnapshot = await getDocs(alertsQuery);
  
      // Use a Set to track unique email addresses
      const uniqueEmails = new Set();
      const sendEmailPromises = [];
  
      alertsSnapshot.forEach((doc) => {
        const userData = doc.data();
  
        // Check if the email is already processed
        if (!uniqueEmails.has(userData.email)) {
          uniqueEmails.add(userData.email); // Add email to the Set
  
          const templateParams = {
            user_name: userData.name,
            event_title: `Event ID: ${selectedEventId}`,
            to_email: userData.email,
            announcement: announcementText,
          };
  
          console.log('Template Params:', templateParams); // Debugging log
  
          sendEmailPromises.push(
            emailjs.send(
              'service_1ag42sn', // Your EmailJS service ID
              'template_n13nj4g', // Your EmailJS template ID
              templateParams,
              '46V6J71EycK6R2OJ7' // Your EmailJS user ID
            )
          );
        }
      });
  
      // Wait for all emails to be sent
      await Promise.all(sendEmailPromises);
  
      alert('Announcement sent successfully!');
      handleCloseAnnouncementModal();
    } catch (error) {
      console.error('Error sending announcement:', error);
      alert('Failed to send announcement.');
    }
  };

  // Helper function to convert military time to standard time
const formatTime = (time) => {
  if (!time) return 'Invalid time';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const standardHours = hours % 12 || 12; // Convert 0 to 12 for midnight
  return `${standardHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

  const handleAnnouncePrize = async (index) => {
    const subField = schedule.subFields[index];
    if (!subField.time || !subField.prize || !subField.winningTicket) {
      alert('Please fill out all fields (time, prize, and winning ticket) before announcing.');
      return;
    }
  
    try {
      const eventRef = doc(db, 'events', selectedEventId);
      const formattedTime = formatTime(subField.time); // Convert time to standard format (AM/PM)
      const announcementText = `${formattedTime} prize for ${subField.prize} has winning ticket ${subField.winningTicket}.`;
      const timestamp = Date.now();
  
      await updateDoc(eventRef, {
        announcement: announcementText,
        announcementTimestamp: timestamp,
      });
  
      alert('Announcement sent successfully!');
    } catch (error) {
      console.error('Error sending announcement:', error);
      alert('Failed to send announcement.');
    }
  };

  const handleDownloadPDF = async (event) => {
    const doc = new jsPDF();
  
    // Add the logo image at the top of the page
    const img = new Image();
    img.src = '/images/kmlogo.png'; // Use the public URL for the logo
  
    img.onload = async () => {
      doc.addImage(img, 'PNG', 80, 10, 50, 30); // Center the logo (x: 80, y: 10, width: 50, height: 30)
  
      // Add centered title
      doc.setFont('helvetica', 'bold'); // Make the text bold
      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      doc.text('Event Information', 105, 50, { align: 'center' });
  
      // Add event details
      doc.setFontSize(14); // Increase font size for better readability
      doc.setTextColor(40, 40, 40); // Dark gray color
      doc.text(`Event Name: ${event.eventName}`, 105, 70, { align: 'center' });
      doc.text(`Event Location: ${event.eventLocation || 'No location provided'}`, 105, 80, { align: 'center' });
      doc.text(`Event ID: ${event.id}`, 105, 90, { align: 'center' });
  
      // Add website link
      doc.setTextColor(0, 0, 255); // Blue color for the link
      doc.text('Website: www.kandmbuzzboard.com', 105, 100, { align: 'center' });
  
      // Generate QR code for the EventDetails page URL
      const eventURL = `https://www.kandmbuzzboard.com/event/${event.id}`; // Direct link to EventDetails page
      const qrCodeDataURL = await QRCode.toDataURL(eventURL);
  
      // Add QR code to the PDF
      doc.addImage(qrCodeDataURL, 'PNG', 80, 110, 50, 50); // Center the QR code (x: 80, y: 110, width: 50, height: 50)
  
      // Add instructions paragraph at the bottom
      doc.setFontSize(12); // Slightly smaller font for instructions
      doc.setTextColor(40, 40, 40); // Dark gray color
      const instructions = [
        'To find your event, simply go to the camera app on your phone',
        'and focus the camera on the QR Code. Click on the link and',
        'you will be directed to the bash hub.',
        'Optionally, visit the website above and type in the event ID',
        'into the "Enter Event ID" box and hit submit.',
      ];
      instructions.forEach((line, index) => {
        doc.text(line, 105, 180 + (index * 8), { align: 'center' }); // Add line breaks
      });
  
      // Save the PDF
      doc.save(`${event.eventName}_EventInfo.pdf`);
    };
  };

  const handleExportEventData = async (eventId) => {
    try {
      const eventRef = doc(db, 'events', eventId); // Use the passed event ID
      const eventDoc = await getDoc(eventRef);
  
      if (!eventDoc.exists()) {
        alert('Event not found.');
        return;
      }
  
      const eventData = eventDoc.data();
  
      // Prepare data for CSV export
      const exportData = [];
  
      // Add event header info
      exportData.push({
        'Information': 'EVENT DETAILS',
        'Details': '',
        'Value': '',
      });
      exportData.push({
        'Information': 'Event Name',
        'Details': eventData.eventName || 'N/A',
        'Value': '',
      });
      exportData.push({
        'Information': 'Event Location',
        'Details': eventData.eventLocation || 'N/A',
        'Value': '',
      });
      exportData.push({
        'Information': 'Event ID',
        'Details': eventId,
        'Value': '',
      });
      exportData.push({
        'Information': '',
        'Details': '',
        'Value': '',
      });
      exportData.push({
        'Information': 'GAMES',
        'Details': 'Serial Number',
        'Value': 'Winning Ticket',
      });
  
      // Add games data
      if (eventData.games && Array.isArray(eventData.games)) {
        eventData.games.forEach((game) => {
          exportData.push({
            'Information': game.name || 'N/A',
            'Details': game.serialNumber || 'N/A',
            'Value': game.winningTicket || 'N/A',
          });
        });
      } else {
        exportData.push({
          'Information': 'No games found',
          'Details': '',
          'Value': '',
        });
      }
  
      // Add schedule section
      exportData.push({
        'Information': '',
        'Details': '',
        'Value': '',
      });
      exportData.push({
        'Information': 'SCHEDULE',
        'Details': 'Time',
        'Value': 'Prize | Winning Ticket',
      });
  
      if (eventData.schedule && eventData.schedule.subFields && Array.isArray(eventData.schedule.subFields)) {
        exportData.push({
          'Information': `Start Time: ${eventData.schedule.startTime || 'N/A'}`,
          'Details': '',
          'Value': '',
        });
        eventData.schedule.subFields.forEach((subField, index) => {
          exportData.push({
            'Information': `Item ${index + 1}`,
            'Details': subField.time || 'N/A',
            'Value': `${subField.prize || 'N/A'} | ${subField.winningTicket || 'N/A'}`,
          });
        });
      } else {
        exportData.push({
          'Information': 'No schedule found',
          'Details': '',
          'Value': '',
        });
      }
  
      // Add winners/announcements section
      exportData.push({
        'Information': '',
        'Details': '',
        'Value': '',
      });
      exportData.push({
        'Information': 'WINNERS/ANNOUNCEMENTS',
        'Details': 'Latest Announcement',
        'Value': '',
      });
  
      if (eventData.announcement) {
        exportData.push({
          'Information': eventData.announcement,
          'Details': eventData.announcementTimestamp ? new Date(eventData.announcementTimestamp).toLocaleString() : 'N/A',
          'Value': '',
        });
      } else {
        exportData.push({
          'Information': 'No announcements yet',
          'Details': '',
          'Value': '',
        });
      }
  
      // Convert to CSV using Papa.unparse
      const csv = Papa.unparse(exportData);
  
      // Trigger download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${eventData.eventName || 'Event'}_EventData.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  
      alert('Event data exported successfully!');
    } catch (error) {
      console.error('Error exporting event data:', error);
      alert('Failed to export event data.');
    }
  };
  const handleDownloadSchedulePDF = async () => {
    if (!selectedEventId) {
      alert('Please select an event to download the schedule.');
      return;
    }
  
    const selectedEvent = events.find((event) => event.id === selectedEventId);
    if (!selectedEvent) {
      alert('Event not found.');
      return;
    }
  
    const doc = new jsPDF();
  
    // Add the logo image at the top of the page
    const img = new Image();
    img.src = '/images/kmlogo.png'; // Use the public URL for the logo
  
    img.onload = () => {
      doc.addImage(img, 'PNG', 80, 10, 50, 30); // Center the logo (x: 80, y: 10, width: 50, height: 30)
  
      // Add centered title
      doc.setFont('helvetica', 'bold'); // Set font to bold
      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      doc.text('Event Schedule', 105, 50, { align: 'center' });
  
      // Add event details
      doc.setFontSize(14);
      doc.text(`Event Name: ${selectedEvent.eventName}`, 20, 70);
      doc.text(`Event Location: ${selectedEvent.eventLocation || 'No location provided'}`, 20, 80);
      doc.text(`Event ID: ${selectedEventId}`, 20, 90);
  
      // Add start time above schedule
      doc.setFontSize(12);
      doc.text(`Start Time: ${schedule.startTime || 'N/A'}`, 20, 100);
  
      // Add schedule details
      doc.text('Schedule:', 20, 110);
      schedule.subFields.forEach((subField, index) => {
        doc.text(
          `${index + 1}. Time: ${subField.time || 'N/A'}, Prize: ${subField.prize || 'N/A'}, Winning Ticket: ${subField.winningTicket || 'N/A'}`,
          20,
          120 + index * 10
        );
      });
  
      // Save the PDF
      doc.save(`${selectedEvent.eventName}_Schedule.pdf`);
    };
  };

  if (loading) {
    return <div className="loading">Loading user data...</div>;
  }

  return (
    <div className="admin-dashboard-container">
      <div className="admin-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p>Here you can manage your events.</p>
        </div>
      </div>

      {user ? (
        <>
          <div className="layout">
            <aside>
              <h3>
                <FaUser style={{ marginRight: '8px', color: 'gold' }} /> My Account
              </h3>
              <div className="account-details">
  {!isEditingProfile ? (
    <>
      <p><strong>Name:</strong> {user.name}</p>
      <p><strong>Company:</strong> {user.company}</p>
      <p><strong>Email:</strong> {user.email}</p>
      <button onClick={() => setIsEditingProfile(true)} className="button">
        Update Profile
      </button>
      <button onClick={handleResetPassword} className="button">
        Reset Password
      </button>
      <button
        onClick={() => navigate('/archived-events')}
        className="button"
      >
        View Archived Events
      </button>
    </>
  ) : (
    <div className="profile-form">
      <input
        type="text"
        name="name"
        placeholder="Name"
        value={profileData.name}
        onChange={handleProfileInputChange}
        className="button"
      />
      <input
        type="text"
        name="company"
        placeholder="Company"
        value={profileData.company}
        onChange={handleProfileInputChange}
        className="button"
      />
      <input
        type="email"
        name="email"
        placeholder="Email"
        value={profileData.email}
        onChange={handleProfileInputChange}
        className="button"
      />
      <button onClick={handleUpdateProfile} className="button">
        Save Changes
      </button>
      <button onClick={() => setIsEditingProfile(false)} className="button">
        Cancel
      </button>
    </div>
  )}
</div>

            </aside>

            <section>
              <h3>
                <FaCalendarAlt style={{ marginRight: '8px', color: 'gold' }} /> My Events
              </h3>
          {events.length > 0 ? (
            <ul className="event-list">
              {events.map((event) => (
                <li key={event.id} className="event-item">
                  <p>
  <FaCalendarAlt style={{ marginRight: '8px', color: 'gold' }} />
  <strong>{event.eventName}</strong>
</p>
<p>
  <FaMapMarkerAlt style={{ marginRight: '8px', color: 'gold' }} />
  Location: {event.eventLocation || 'No location provided'}
</p>
<p>
  <FaIdBadge style={{ marginRight: '8px', color: 'gold' }} />
  ID: {event.id}
</p>

                <div className="event-actions">
                  <button
                    onClick={() => handleDeleteEventClick(event.id)}
                    className="button"
                  >
                    Delete Event
                  </button>

                  <button
                    onClick={() => hiddenEvents[event.id] ? handleShowEvent(event.id) : handleHideEvent(event.id)}
                    className="button ghost"
                  >
                    {hiddenEvents[event.id] ? 'Show' : 'Hide'}
                  </button>

                  <button
                    onClick={() => handlePresentEvent(event.id)}
                    className="button ghost"
                  >
                    Go To Event
                  </button>

                  <button
                    onClick={() => handleExportEventData(event.id)}
                    className="button ghost"
                  >
                    Export Event Data
                  </button>

                  <button
                    onClick={() => handleEditSchedule(event.id)}
                    className="button ghost"
                  >
                    Edit Event Schedule
                  </button>

                  <button
                    onClick={() => handleDeleteScheduleClick(event.id)}
                    className="button ghost"
                  >
                    Delete Schedule
                  </button>

                  <button
                    onClick={() => navigate('/poster')}
                    className="button ghost"
                  >
                   Create Event Poster
                  </button>

                  <button
                    onClick={() => { setSelectedEventId(event.id); handleOpenAnnouncementModal(); }}
                    className="button"
                  >
                    Make Announcement
                  </button>

                  <button
                    onClick={() => handleDownloadPDF(event)}
                    className="button ghost"
                  >
                    Event QR Code Download 
                  </button>
                </div>

                {isEventModalOpen && (
                  <div className="modal">
                    <div className="modal-content">
                      <p className='areyou'>Are you sure you want to delete this event?</p>
                      <div className="modal-buttons">
                        <button onClick={confirmDeleteEvent} className="confirm-button">
                          Yes, Delete
                        </button>
                        <button onClick={closeEventModal} className="cancel-button">
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {isAnnouncementModalOpen && (
                  <div className="modal">
                    <div className="modal-content">
                      <h2>Make Announcement</h2>
                      <textarea
                        value={announcementText}
                        onChange={(e) => setAnnouncementText(e.target.value)}
                        placeholder="Type your announcement here..."
                        className="announcement-textarea"
                      />
                      <div className="modal-buttons">
                        <button
                          onClick={() => handleSendAnnouncement(selectedEventId)}
                          className="confirm-button"
                        >
                          Send Announcement
                        </button>
                        <button
                          onClick={handleCloseAnnouncementModal}
                          className="cancel-button"
                        >
                          Close Announcement
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {isScheduleModalOpen && (
                  <div className="modal">
                    <div className="modal-content">
                      <p className='areyou'>Are you sure you want to delete this schedule?</p>
                      <div className="modal-buttons">
                        <button onClick={confirmDeleteSchedule} className="confirm-button">
                          Yes, Delete
                        </button>
                        <button onClick={closeScheduleModal} className="cancel-button">
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                </li>
              ))}
            </ul>
          ) : (
            <p>You have not created any events yet.</p>
          )}
            </section>
          </div>

{selectedEventId && (
  <div className="schedule-form">
    <h3>Schedule for Event ID: {selectedEventId}</h3>
    <label className="start-time-label">
  <FaClock style={{ marginRight: '8px', color: 'gold' }} /> Start Time:
  <input
    type="time"
    value={schedule.startTime}
    onChange={(e) =>
      setSchedule((prev) => ({ ...prev, startTime: e.target.value }))
    }
    className="button"
  />
</label>
<h4>
  <FaList style={{ marginRight: '8px', color: 'gold' }} /> Subfields:
</h4>
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="subFields">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {schedule.subFields.map((subField, index) => (
              <Draggable key={index} draggableId={`${index}`} index={index}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className="subfield"
                  >
                    <input
                      type="time"
                      placeholder="Time"
                      value={subField.time}
                      onChange={(e) =>
                        handleScheduleChange(index, 'time', e.target.value)
                      }
                      className="button"
                    />
                    <input
                      type="text"
                      placeholder="Prize"
                      value={subField.prize}
                      onChange={(e) =>
                        handleScheduleChange(index, 'prize', e.target.value)
                      }
                      className="button"
                    />
                    <input
                      type="text"
                      placeholder="Winning Ticket"
                      value={subField.winningTicket}
                      onChange={(e) =>
                        handleScheduleChange(index, 'winningTicket', e.target.value)
                      }
                      className="button"
                    />
                    <button
                      onClick={() => handleRemoveSubField(index)}
                      className="button"
                    >
                      Remove
                    </button>
                    <button
  onClick={() => handleAnnouncePrize(index)}
  className="button"
>
  Announce Prize
</button>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
    <button onClick={handleAddSubField} className="button">Add Subfield</button>
    <h4>
  <FaImage style={{ marginRight: '8px', color: 'gold' }} /> Or Upload Schedule Image:
</h4>
    <input type="file" onChange={handleImageUpload} className="button" />
    {uploadedImage && (
      <div className="uploaded-image-preview">
        <p>Uploaded Image:</p>
        <img
          src={URL.createObjectURL(uploadedImage)}
          alt="Uploaded Schedule"
          className="uploaded-image"
        />
        <button onClick={handleDeleteImage} className="button">Delete Image</button>
      </div>
    )}
   {savedImage && (
  <div className="saved-image-preview">
    <p>Saved Image:</p>
    <img
      src={savedImage}
      alt="Saved Schedule"
      className="saved-image"
    />
    <button onClick={handleDeleteImage} className="button">Delete Saved Image</button>
  </div>
)}
    <button onClick={handleSaveSchedule} className="button">Save Schedule</button>
    <button onClick={handleDownloadSchedulePDF} className="button">Download Schedule as PDF</button>
  </div>
)}
        </>
      ) : (
        <p>No user data available. Please log in.</p>
      )}
    </div>
  );
}

export default AdminDashboard;